import React, { useEffect, useMemo, useRef,useState } from "react";
import { useParams } from "react-router-dom";
import Highcharts from "highcharts";
import { Box, Card, Typography } from "@mui/material";
import HighchartsReact from "highcharts-react-official";
import Drilldown from "highcharts/modules/drilldown";
import {
  useGetPhaseSummaryQuery,
  useLazyGetTypeSummaryByPhaseQuery,
} from "store/slices/apis/statutoryApprovalSlice";
import ApprovalTrackerListPopUp from "./ApprovalTrackerListPopup";


//Drilldown(Highcharts);

const toArray = (v) => (Array.isArray(v) ? v : Array.isArray(v?.data) ? v.data : []);

export default function CategoryCount(columns) {
    const pieColors = [
    "#2CAFEE",
    "#57C1F3",
    "#80D2F8",
    "#AADFFB",
    "#1E96E1",
    "#147BC3",
    "#0C62A0",
    "#084B82",
    ];
    
    const { projectId } = useParams();
  
    const [selectedPhase, setSelectedPhase] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [isViewTracker, setIsViewTracker] = useState(false);

    const handleClose = () => setIsViewTracker(false);

  const chartRef = useRef(null);
  const addedSeries = useRef(new Set());

  const { data: phaseResp, isFetching: phasesFetching } =
    useGetPhaseSummaryQuery(projectId);

  // ✅ Correct lazy hook usage:
  const [triggerTypes] = useLazyGetTypeSummaryByPhaseQuery();

  // reset added-series cache when project changes
  useEffect(() => {
    addedSeries.current = new Set();
  }, [projectId]);

  const phases = useMemo(() => toArray(phaseResp), [phaseResp]);

  const options = useMemo(() => ({
    chart: { type: "pie", height: 320, backgroundColor: "#FFFFFF" },
    colors: pieColors,
    title: { text: "" },
    subtitle: { text: "Click a Phase to drill down by Category" },
    tooltip: {
        formatter: function () {
            return `<b>${this.point.name}</b><br/>
            Count: <b>${this.point.y}</b><br/>
            Percentage: <b>${this.point.percentage.toFixed(1)}%</b><br/>
            ${this.point.description || ''}`;
        },
        useHTML: true
    },
    legend: {
        enabled: true,
        layout: "vertical",
        align: "right",
        verticalAlign: "middle",
        itemMarginTop: 6,
        itemMarginBottom: 6,
        labelFormatter: function () {
            return `${this.name}: <b>${this.y}</b>`;
        }
    },
    plotOptions: {
      pie: {
        cursor: "pointer",
        dataLabels: {
            enabled: true,
            format: '<b>{point.name}</b><br/>{point.y} ({point.percentage:.1f}%)',
            distance: 30,
            style: {
                fontWeight: 'normal',
                fontSize: '12px'
            }
        },
        point:{
            events: {
                click: function (event) {
                    if (this.drilldown) return;
                    // Type slice (drilldown level) -> open popup
                    const type = this.name;

                    setSelectedCategory(type);
                    setIsViewTracker(true);

                    // prevent any default behaviour
                    if (event?.preventDefault) event.preventDefault();
                }
            }
        },
      },
    },
    series: [{
      name: "Phase",
      colorByPoint: true,
      data: phases.map(p => {
        const phase = (p.category || "Unknown").trim();
        return { name: phase, y: p.count || 0, drilldown: `phase:${phase}` };
      }),
    }],
    drilldown: { series: [] },
    //colors: pieData.map(item => item.color),
    credits: {
        enabled: false
    }
  }), [phases]);

  const cacheRef = useRef({}); // phase -> rows

const onDrilldown = async (e) => {
  const chart = chartRef.current?.chart;
  if (!chart) return;

  const phase = e.point?.name || "Unknown";
  setSelectedPhase(phase);
  const drillId = e.point?.drilldown;
  if (!drillId) return;

  chart.showLoading("Loading...");

  try {
    let rows = cacheRef.current[phase];

    if (!rows) {
      const res = await triggerTypes({ projectId, phase }).unwrap();
      rows = Array.isArray(res) ? res : (res?.data || []);
      cacheRef.current[phase] = rows;
    }

    chart.addSeriesAsDrilldown(e.point, {
      id: drillId,
      name: `Types (${phase})`,
      data: rows.map(r => [
        (r.category || "Unknown").trim(),
        Number(r.count || 0),
      ]),
    });
  } finally {
    chart.hideLoading();
  }
};


  return (
        <>
            <Card
                elevation={0}
                sx={{
                    borderRadius: "3px",
                    border: "1px solid #E2E8F0",
                    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05)",
                    width: '100%',
                    height: 400,
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: '#ffffff',
                }}
            >
                <Box sx={{
                    width: '100%',
                    borderBottom: "1px solid #E4E4E4",
                    px: 1.5,
                    py: 1.25,
                    mb: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxSizing: 'border-box',
                    minHeight: 70,
                }}>
                    <Box>
                        <Typography variant="subtitle1" sx={{
                            fontFamily: "Outfit, sans-serif",
                            fontWeight: 600,
                            fontSize: '15px',
                            lineHeight: '21px',
                            letterSpacing: "0px",
                            mb: 0.5
                        }}>
                            Category Distribution by Phase
                        </Typography>
                        <Typography variant="subtitle1" sx={{
                            fontWeight: 500,
                            fontSize: '11px',
                            lineHeight: "16px",
                            color: "#7D7F81",
                            letterSpacing: "1%",
                            fontFamily: "Poppins, sans-serif",
                        }}>
                            Count of Statutory Approvals with drill down distribution by phase and category
                        </Typography>
                    </Box>
                </Box>

                {!phasesFetching && phases?.length> 0 ? (
                    <HighchartsReact 
                        highcharts={Highcharts} 
                        options={options}
                        ref={chartRef}
                        callback={(chart) => {
                        if (!chart.__ddAttached) {
                            chart.__ddAttached = true;
                            Highcharts.addEvent(chart, "drilldown", onDrilldown);
                        }
                        }}
                    />
                ) : (
                    <Box
                        sx={{
                            height: 320,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            pb: 2,
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: "Outfit, sans-serif",
                                fontSize: "16px",
                                fontWeight: 500,
                                color: "#9E9E9E",
                            }}
                        >
                            Data Not Available
                        </Typography>
                    </Box>
                )}
            </Card>

            {isViewTracker && (
                <ApprovalTrackerListPopUp
                    open={isViewTracker}
                    data={{ Phase: selectedPhase,StatutoryTrackerType: selectedCategory }}
                    handleClose={handleClose}
                    columns={columns.columns}
                />
            )}
        </>
    );
}
